import InputField from "components/Common/InputField"
import ThemeModal from "components/Common/ThemeModal"
import FormikCreatableSelect from "components/Common/FormikCreatableSelect"
import VoucherImageUpload from "components/Common/VoucherImageUpload"
import { useMemo, useCallback, useState, useEffect } from "react"
import { toast } from "react-toastify"
import { Form, FormFeedback, Label, Button, Row, Col, Card, CardBody } from "reactstrap"
import * as Yup from "yup"
import { useFormik } from "formik"
import { useAccountLoans } from "hooks/useAccountLoan"
import { useAccountPaymentMethods } from "hooks/useAccountPaymentMethod"
import { useAccounts } from "hooks/useAccountEntity"
import { useCreateAccountLoanRepayment } from "hooks/useAccountLoanRepayment"

const AccountLoanRepaymentAddEdit = ({ modal, toggleModal, isEdit, editedData }) => {
  const { mutateAsync: createMutation, isPending: createLoading } =
    useCreateAccountLoanRepayment()

  const [voucherImages, setVoucherImages] = useState(editedData?.images || [])

  useEffect(() => {
    setVoucherImages(editedData?.images || [])
  }, [editedData])

  const { data: loanResponse, isLoading: loanLoading } = useAccountLoans(
    { all: true, per_page: 200 },
    modal
  )
  const { data: paymentMethodResponse, isLoading: paymentMethodLoading } =
    useAccountPaymentMethods({ all: true, per_page: 200 }, modal)
  const { data: accountResponse, isLoading: accountLoading } =
    useAccounts({ all: true, per_page: 200 }, modal)

  const loans = loanResponse?.data?.data || loanResponse?.data || []
  const paymentMethods = paymentMethodResponse?.data?.data || paymentMethodResponse?.data || []
  const accounts = accountResponse?.data?.data || accountResponse?.data || []

  const loanOptions = useMemo(
    () =>
      Array.isArray(loans)
        ? loans.map(item => ({
            value: item.id,
            label: `${item.borrower?.name || "Borrower"} - ${item.amount || 0}`,
          }))
        : [],
    [loans]
  )

  const paymentMethodOptions = useMemo(
    () =>
      Array.isArray(paymentMethods)
        ? paymentMethods.map(item => ({ value: item.id, label: item.name }))
        : [],
    [paymentMethods]
  )

  const accountOptions = useMemo(
    () => (Array.isArray(accounts) ? accounts.map(item => ({ value: item.id, label: item.name })) : []),
    [accounts]
  )

  const validationSchema = Yup.object({
    loan_id: Yup.mixed().required("Please select a loan"),
    amount: Yup.number().typeError("Amount must be numeric").integer("Amount must be a whole number").min(1, "Amount must be at least 1").required("Please enter repayment amount"),
    payment_method_id: Yup.mixed().required("Please select a payment method"),
    account_id: Yup.mixed().required("Please select an account"),
    repayment_date: Yup.string().required("Please select the repayment date"),
    notes: Yup.string().nullable(),
  })

  const onSubmit = useCallback(
    async (values, { resetForm }) => {
      const payload = {
        ...values,
        amount: Number(values.amount),
        notes: values.notes?.trim() || null,
        image_ids: voucherImages.map(img => img.id),
      }

      try {
        const res = await createMutation(payload)
        toast.success(res?.message ?? "Loan repayment created successfully.")
        resetForm()
        toggleModal()
      } catch (error) {
        const msg = error?.response?.data?.message || "Failed to save loan repayment."
        toast.error(msg)
      }
    },
    [createMutation, toggleModal, voucherImages]
  )

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      loan_id: editedData?.loan_id || editedData?.loan?.id || "",
      amount: editedData?.amount ?? "",
      payment_method_id: editedData?.payment_method_id || editedData?.paymentMethod?.id || "",
      account_id: editedData?.account_id || editedData?.account?.id || "",
      repayment_date: editedData?.repayment_date || "",
      notes: editedData?.notes || "",
    },
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit,
  })

  const loadingAny = createLoading || loanLoading || paymentMethodLoading || accountLoading

  return (
    <ThemeModal isOpen={modal} toggle={toggleModal} title={isEdit ? "View Loan Repayment" : "Add Loan Repayment"} size="xl">
      <Form onSubmit={e => { e.preventDefault(); formik.handleSubmit() }}>
        <Card className="border">
          <CardBody>
            <h5 className="mb-3">Repayment Information</h5>
            <Row>
              <Col md={6}><FormikCreatableSelect name="loan_id" formik={formik} label="Loan" options={loanOptions} loading={loanLoading} required /></Col>
              <Col md={6}><div className="mb-3"><Label htmlFor="amount">Amount</Label><InputField type="number" name="amount" id="amount" min="1" value={formik.values.amount} onChange={formik.handleChange} invalid={formik.touched.amount && !!formik.errors.amount} />{formik.touched.amount && formik.errors.amount && <FormFeedback type="invalid">{formik.errors.amount}</FormFeedback>}</div></Col>
            </Row>
            <Row>
              <Col md={6}><FormikCreatableSelect name="payment_method_id" formik={formik} label="Payment Method" options={paymentMethodOptions} loading={paymentMethodLoading} required /></Col>
              <Col md={6}><FormikCreatableSelect name="account_id" formik={formik} label="Account" options={accountOptions} loading={accountLoading} required /></Col>
            </Row>
            <Row>
              <Col md={12}><div className="mb-3"><Label htmlFor="repayment_date">Repayment Date</Label><InputField type="date" name="repayment_date" id="repayment_date" value={formik.values.repayment_date} onChange={formik.handleChange} invalid={formik.touched.repayment_date && !!formik.errors.repayment_date} />{formik.touched.repayment_date && formik.errors.repayment_date && <FormFeedback type="invalid">{formik.errors.repayment_date}</FormFeedback>}</div></Col>
            </Row>
          </CardBody>
        </Card>

        <Card className="border mt-3">
          <CardBody>
            <h5 className="mb-3">Additional Notes</h5>
            <div className="mb-3"><Label htmlFor="notes">Notes</Label><InputField type="textarea" name="notes" id="notes" value={formik.values.notes} onChange={formik.handleChange} invalid={formik.touched.notes && !!formik.errors.notes} />{formik.touched.notes && formik.errors.notes && <FormFeedback type="invalid">{formik.errors.notes}</FormFeedback>}</div>
            <VoucherImageUpload images={voucherImages} onChange={setVoucherImages} />
          </CardBody>
        </Card>

        <div className="text-end mt-3">
          <Button color="success" type="submit" disabled={loadingAny}>
            {loadingAny ? "Saving..." : "Add Loan Repayment"}
          </Button>
        </div>
      </Form>
    </ThemeModal>
  )
}

export default AccountLoanRepaymentAddEdit
